// EHR demo cases for the hero animation. Each case cycles through:
// typing → suggestions populate → primary confirmed. Patients use first
// initial + surname as light pop-culture references. Codes are real WHO
// ICD-10 (the version Landspítali uses), not the US ICD-10-CM variant.

export type EhrSuggestion = {
  code: string;
  desc: string;
  conf: number;
  primary?: boolean;
};

export type EhrCase = {
  id: string;
  patient: string;
  encounter: string;
  department: string;
  template: string;
  noteLines: string[];
  suggestions: EhrSuggestion[];
};

export const EHR_CASES: EhrCase[] = [
  {
    id: 'ent-postop',
    patient: 'Trial Patient 2',
    encounter: 'Admission 12.12.2025 · ENT',
    department: 'ENT / Otolaryngology',
    template: 'Post-op follow-up',
    noteLines: [
      'Post-op Day 1.',
      'Right modified radical mastoidectomy with tympanoplasty.',
      '',
      'Patient reports mild ear pain, well-controlled with oral analgesics.',
      'No vertigo or facial weakness. Minimal serosanguinous drainage.',
      'Packing intact. Wound clean, no signs of infection.',
      '',
      'A/P:',
      '- Continue current antibiotics and ear drops.',
      '- Follow-up in ENT clinic in 2 weeks.',
    ],
    suggestions: [
      { code: 'H72.9',   desc: 'Perforation of tympanic membrane, unspecified', conf: 0.94 },
      { code: 'Z09.0',   desc: 'Follow-up examination after surgery for other conditions', conf: 0.91, primary: true },
      { code: 'H74.1',   desc: 'Adhesive middle ear disease', conf: 0.78 },
      { code: 'H66.9',   desc: 'Otitis media, unspecified', conf: 0.66 },
      { code: 'Z48.8',   desc: 'Other specified surgical follow-up care', conf: 0.61 },
    ],
  },
  {
    id: 'sleep-mcfly',
    patient: 'M. McFly',
    encounter: 'Outpatient 26.10.2025 · Sleep',
    department: 'Sleep Medicine',
    template: 'Sleep consultation',
    noteLines: [
      '30yo M, self-referred for sleep evaluation.',
      '',
      'Reports severe disruption to sleep/wake cycle after recent',
      'long-distance displacement across several time zones.',
      'Intermittent auditory déjà vu, dreams feel "non-linear".',
      'Resting HR 94.',
      '',
      'A/P: controlled light exposure, trial of low-dose melatonin.',
    ],
    suggestions: [
      { code: 'G47.2',   desc: 'Disorders of the sleep-wake schedule', conf: 0.93, primary: true },
      { code: 'F51.2',   desc: 'Nonorganic disorder of the sleep-wake schedule', conf: 0.85 },
      { code: 'R00.0',   desc: 'Tachycardia, unspecified', conf: 0.68 },
      { code: 'F51.8',   desc: 'Other nonorganic sleep disorders', conf: 0.59 },
      { code: 'Z71.9',   desc: 'Counselling, unspecified', conf: 0.52 },
    ],
  },
  {
    id: 'ortho-skywalker',
    patient: 'L. Skywalker',
    encounter: 'Outpatient 04.01.2026 · Ortho',
    department: 'Orthopedic Surgery',
    template: 'Post-op follow-up',
    noteLines: [
      'Post-op Day 7.',
      'S/P right wrist disarticulation, high-energy thermal injury.',
      '',
      'Stump healing appropriately, no signs of infection.',
      'Prosthetic fitting initiated, myoelectric interface preliminary.',
      'Pt reports occasional phantom sensation in absent hand.',
      '',
      'A/P: continue prosthesis training, follow-up in 2 weeks.',
    ],
    suggestions: [
      { code: 'S68.4',   desc: 'Traumatic amputation of hand at wrist level', conf: 0.95, primary: true },
      { code: 'Z44.0',   desc: 'Fitting and adjustment of artificial arm', conf: 0.88 },
      { code: 'G54.7',   desc: 'Phantom limb syndrome without pain', conf: 0.77 },
      { code: 'Z48.8',   desc: 'Other specified surgical follow-up care', conf: 0.69 },
      { code: 'Z09.0',   desc: 'Follow-up examination after surgery for other conditions', conf: 0.55 },
    ],
  },
  {
    id: 'sports-potter',
    patient: 'J. Potter',
    encounter: 'Outpatient 18.11.2025 · Sports',
    department: 'Sports Medicine',
    template: 'Injury follow-up',
    noteLines: [
      '17yo M, school athlete.',
      '',
      'Blunt impact to head during recreational flying-sports match',
      '(high-velocity ball-like projectile).',
      'Brief LOC <60s, transient confusion.',
      'CT head: no intracranial pathology.',
      '',
      'A/P: graduated return-to-play concussion protocol.',
    ],
    suggestions: [
      { code: 'S06.0',   desc: 'Concussion', conf: 0.87, primary: true },
      { code: 'S09.9',   desc: 'Unspecified injury of head', conf: 0.82 },
      { code: 'W21.3',   desc: 'Struck by sports equipment, sports area', conf: 0.78 },
      { code: 'S00.9',   desc: 'Superficial injury of head, part unspecified', conf: 0.62 },
      { code: 'R41.8',   desc: 'Other symptoms involving cognitive functions and awareness', conf: 0.54 },
    ],
  },
  {
    id: 'id-baggins',
    patient: 'F. Baggins',
    encounter: 'Outpatient 06.03.2026 · ID',
    department: 'Infectious Disease',
    template: 'Chronic wound consult',
    noteLines: [
      '50yo M, 3-week-old puncture wound R shoulder.',
      'Weapon reported as "cold, bladed, unknown metallurgy".',
      '',
      'Slow, stippled healing despite course of IV antibiotics.',
      'Chronic fatigue, low-grade pyrexia, vivid nightmares.',
      'Pt preoccupied with a "lost heirloom".',
      '',
      'A/P: wound biopsy, trauma counselling referral.',
    ],
    suggestions: [
      { code: 'T14.9',   desc: 'Injury, unspecified', conf: 0.81, primary: true },
      { code: 'S41.0',   desc: 'Open wound of shoulder', conf: 0.74 },
      { code: 'R50.9',   desc: 'Fever, unspecified', conf: 0.69 },
      { code: 'F51.5',   desc: 'Nightmares', conf: 0.62 },
      { code: 'R53',     desc: 'Malaise and fatigue', conf: 0.48 },
    ],
  },
  {
    id: 'ed-rose',
    patient: 'R. DeWitt Bukater',
    encounter: 'ED 15.04.1912 · Trauma',
    department: 'Emergency Medicine',
    template: 'Trauma resuscitation',
    noteLines: [
      '22yo F, extricated after maritime incident, N. Atlantic.',
      '',
      'Core temp 32.1°C on arrival. Shivering +, bradycardic.',
      'Active external rewarming initiated.',
      '',
      'Acute grief reaction noted; travelling companion deceased at scene.',
      'A/P: ICU admission, mental-health consult.',
    ],
    suggestions: [
      { code: 'T68',     desc: 'Hypothermia', conf: 0.93, primary: true },
      { code: 'R40.0',   desc: 'Somnolence', conf: 0.72 },
      { code: 'Z63.4',   desc: 'Disappearance and death of family member', conf: 0.68 },
      { code: 'T69.8',   desc: 'Other specified effects of reduced temperature', conf: 0.63 },
      { code: 'F43.0',   desc: 'Acute stress reaction', conf: 0.55 },
    ],
  },
  {
    id: 'uc-schrute',
    patient: 'D. Schrute',
    encounter: 'Walk-in 13.02.2026 · UC',
    department: 'Urgent Care',
    template: 'Minor injury',
    noteLines: [
      '38yo M, office worker.',
      '',
      'R-side facial abrasion sustained during enthusiastic CPR',
      'training demo on a rubber simulator (colleagues present).',
      'No LOC, no airway compromise.',
      'Wound cleaned and dressed. Tetanus up to date.',
      '',
      'A/P: review manufacturer guidance with HR.',
    ],
    suggestions: [
      { code: 'S00.8',   desc: 'Superficial injury of other parts of head', conf: 0.91, primary: true },
      { code: 'W22.5',   desc: 'Struck by other objects, trade and service area', conf: 0.75 },
      { code: 'S00.0',   desc: 'Superficial injury of scalp', conf: 0.59 },
      { code: 'Z57.8',   desc: 'Occupational exposure to other risk-factors', conf: 0.47 },
      { code: 'R46.8',   desc: 'Other symptoms involving appearance and behaviour', conf: 0.34 },
    ],
  },
  {
    id: 'peds-mccallister',
    patient: 'K. McCallister',
    encounter: 'Outpatient 26.12.2025 · Peds',
    department: 'Pediatrics / Social Work',
    template: 'Safeguarding referral',
    noteLines: [
      '8yo M, brought by law enforcement.',
      '48h unsupervised in single-family home during family travel.',
      '',
      'No injuries to patient. Reports two intruders sustained',
      'significant self-inflicted injuries on household traps.',
      '',
      'A/P: social work and child protective services consulted.',
    ],
    suggestions: [
      { code: 'Z62.0',   desc: 'Inadequate parental supervision and control', conf: 0.92, primary: true },
      { code: 'T74.0',   desc: 'Neglect or abandonment', conf: 0.71 },
      { code: 'Z60.2',   desc: 'Living alone', conf: 0.64 },
      { code: 'Z65.3',   desc: 'Problems related to other legal circumstances', conf: 0.55 },
      { code: 'Z71.9',   desc: 'Counselling, unspecified', conf: 0.42 },
    ],
  },
  {
    id: 'ed-spongebob',
    patient: 'S. SquarePants',
    encounter: 'ED 22.07.2025 · Bikini Bottom',
    department: 'Emergency Medicine',
    template: 'Fluid resuscitation',
    noteLines: [
      'Adult M, presents with severe dehydration after prolonged',
      'separation from primary aqueous environment.',
      '',
      'Integument markedly dry, mucous membranes desiccated.',
      'BP 96/62, HR 108.',
      'IV fluid resuscitation initiated.',
      '',
      'A/P: admit for rehydration, history of similar episodes.',
    ],
    suggestions: [
      { code: 'E86',     desc: 'Volume depletion', conf: 0.95, primary: true },
      { code: 'T67.0',   desc: 'Heatstroke and sunstroke', conf: 0.78 },
      { code: 'L85.3',   desc: 'Xerosis cutis', conf: 0.72 },
      { code: 'R41.0',   desc: 'Disorientation, unspecified', conf: 0.58 },
      { code: 'Z72.8',   desc: 'Other problems related to lifestyle', conf: 0.41 },
    ],
  },
  {
    id: 'im-mario',
    patient: 'M. Mario',
    encounter: 'Outpatient 10.02.2026 · IM',
    department: 'Internal Medicine',
    template: 'Ingestion consult',
    noteLines: [
      '40yo M, plumber by occupation.',
      '',
      'GI upset after ingestion of unidentified red-capped fungus',
      'found near workplace.',
      'Reports transient subjective episodes of "doubling in size".',
      'No vomiting. Mild lethargy.',
      'Serum toxicology pending.',
      '',
      'A/P: observe, supportive care, tox follow-up.',
    ],
    suggestions: [
      { code: 'T62.0',   desc: 'Toxic effect of ingested mushrooms', conf: 0.86, primary: true },
      { code: 'R10.4',   desc: 'Other and unspecified abdominal pain', conf: 0.71 },
      { code: 'R42',     desc: 'Dizziness and giddiness', conf: 0.64 },
      { code: 'F22.0',   desc: 'Delusional disorder', conf: 0.51 },
      { code: 'T65.9',   desc: 'Toxic effect of unspecified substance', conf: 0.42 },
    ],
  },
  {
    id: 'sleep-scrooge',
    patient: 'E. Scrooge',
    encounter: 'Outpatient 25.12.2025 · Sleep',
    department: 'Sleep Medicine',
    template: 'Parasomnia consult',
    noteLines: [
      '82yo M, retired banker.',
      '',
      'Presents with three successive vivid visual hallucinations',
      'in a single overnight period (evening of 24.12).',
      'Each apparition distinct (retrograde, present, prospective).',
      'Denies alcohol or substance use. CBC within normal limits.',
      '',
      'A/P: EEG, outpatient psych consult, sleep diary.',
    ],
    suggestions: [
      { code: 'R44.1',   desc: 'Visual hallucinations', conf: 0.88, primary: true },
      { code: 'G47.0',   desc: 'Disorders of initiating and maintaining sleep', conf: 0.72 },
      { code: 'G47.8',   desc: 'Other sleep disorders', conf: 0.66 },
      { code: 'F22.0',   desc: 'Delusional disorder', conf: 0.53 },
      { code: 'R41.8',   desc: 'Other symptoms involving cognitive functions and awareness', conf: 0.41 },
    ],
  },
  {
    id: 'ortho-geller',
    patient: 'R. Geller',
    encounter: 'Outpatient 17.09.2025 · Ortho',
    department: 'Orthopedics',
    template: 'Musculoskeletal consult',
    noteLines: [
      '38yo M, palaeontologist.',
      '',
      'Acute severe cervicalgia after moving new sofa up stairwell.',
      'Pt reports repeated verbal cues to companions during task.',
      'Limited neck ROM. No focal neurological deficit.',
      '',
      'A/P: NSAIDs, physical therapy referral, soft collar PRN.',
    ],
    suggestions: [
      { code: 'M54.2',   desc: 'Cervicalgia', conf: 0.89, primary: true },
      { code: 'M54.1',   desc: 'Radiculopathy', conf: 0.71 },
      { code: 'S13.4',   desc: 'Sprain and strain of cervical spine', conf: 0.66 },
      { code: 'M79.2',   desc: 'Neuralgia and neuritis, unspecified', conf: 0.55 },
      { code: 'F43.2',   desc: 'Adjustment disorders', conf: 0.38 },
    ],
  },
  {
    id: 'peds-psych-byers',
    patient: 'W. Byers',
    encounter: 'Outpatient 06.11.2025 · Peds Psych',
    department: 'Pediatric Psychiatry',
    template: 'Crisis intake',
    noteLines: [
      '12yo M, brought by caregiver.',
      '',
      'Acute stress reaction following traumatic incident',
      'involving an unknown predator.',
      'Intermittent epistaxis, temperature intolerance.',
      'Social withdrawal, vivid intrusive imagery reported by family.',
      '',
      'A/P: trauma-focused CBT referral, follow-up in 1 week.',
    ],
    suggestions: [
      { code: 'F43.0',   desc: 'Acute stress reaction', conf: 0.84, primary: true },
      { code: 'F43.1',   desc: 'Post-traumatic stress disorder', conf: 0.73 },
      { code: 'R04.0',   desc: 'Epistaxis', conf: 0.64 },
      { code: 'F93.0',   desc: 'Separation anxiety disorder of childhood', conf: 0.58 },
      { code: 'Z63.7',   desc: 'Other stressful life events affecting family and household', conf: 0.44 },
    ],
  },
  {
    id: 'psych-wayne',
    patient: 'B. Wayne',
    encounter: 'Outpatient 31.10.2025 · Psych',
    department: 'Psychiatry / Pulmonology',
    template: 'Adjustment consult',
    noteLines: [
      '34yo M, well-built, self-referred.',
      '',
      'Persistent hypervigilance and insomnia following recurrent',
      'nocturnal activity in a damp subterranean environment.',
      'C/o cough and mild dyspnea from inhalational exposure (guano dust).',
      'Declines family history discussion.',
      '',
      'A/P: HRCT chest, psychotherapy referral.',
    ],
    suggestions: [
      { code: 'F43.22',  desc: 'Adjustment disorder, mixed anxiety and depressive reaction', conf: 0.79, primary: true },
      { code: 'J67.9',   desc: 'Hypersensitivity pneumonitis due to unspecified organic dust', conf: 0.72 },
      { code: 'Z63.4',   desc: 'Disappearance and death of family member', conf: 0.65 },
      { code: 'F51.0',   desc: 'Nonorganic insomnia', conf: 0.58 },
      { code: 'R06.0',   desc: 'Dyspnoea', conf: 0.46 },
    ],
  },
  {
    id: 'trauma-snow',
    patient: 'J. Snow',
    encounter: 'ED 04.11.2025 · Trauma',
    department: 'Trauma / Critical Care',
    template: 'Trauma review',
    noteLines: [
      '25yo M, BIB companions after multiple penetrating wounds',
      'to chest and abdomen. Found unresponsive in snowbank.',
      '',
      'Witnesses describe prior confrontational interaction.',
      'On regaining consciousness, pt states he "knew nothing"',
      'about his attackers.',
      '',
      'A/P: OR, ICU admission, trauma workup.',
    ],
    suggestions: [
      { code: 'T07',     desc: 'Unspecified multiple injuries', conf: 0.82, primary: true },
      { code: 'S21.9',   desc: 'Open wound of thorax, part unspecified', conf: 0.74 },
      { code: 'S31.1',   desc: 'Open wound of abdominal wall', conf: 0.68 },
      { code: 'T68',     desc: 'Hypothermia', conf: 0.60 },
      { code: 'R40.2',   desc: 'Coma, unspecified', conf: 0.52 },
    ],
  },
  {
    id: 'occ-white',
    patient: 'W. White',
    encounter: 'Outpatient 19.05.2025 · Occ',
    department: 'Occupational Medicine',
    template: 'Exposure consult',
    noteLines: [
      '50yo M, high-school chemistry teacher.',
      '',
      'Re-exposure to amine vapors in a poorly ventilated off-site lab.',
      'C/o conjunctival irritation, cough, mild dyspnea.',
      'Denies occupational misuse. HR has flagged concerns.',
      '',
      'A/P: engineering controls review, PPE reinforcement.',
    ],
    suggestions: [
      { code: 'Z57.5',   desc: 'Occupational exposure to toxic agents in other industries', conf: 0.76, primary: true },
      { code: 'J68.3',   desc: 'Acute respiratory conditions due to chemicals, gases and fumes', conf: 0.70 },
      { code: 'H10.2',   desc: 'Other acute conjunctivitis', conf: 0.62 },
      { code: 'R06.0',   desc: 'Dyspnoea', conf: 0.55 },
      { code: 'Z73.0',   desc: 'Burn-out', conf: 0.38 },
    ],
  },
];
